import { useDispatch } from 'react-redux';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';

import useSettings from '../../hooks/useSettings';
import { alertLocalizedMsg } from '../../Utils';
import { uploadFiles, type MiddlewhereFile } from '../FilesUtils';
import { BaseSearch } from '../../filter/Search';
import Icon from '../../Icon';

interface FilesExplorerHeaderProps {
	folderPath: string;
	currentPath: string;
	setCurrentPath: React.Dispatch<React.SetStateAction<string>>;
	currentSearch?: string;
	setCurrentSearch: React.Dispatch<React.SetStateAction<string | undefined>>;
	setSelectedFile: React.Dispatch<React.SetStateAction<MiddlewhereFile | undefined>>;
	updateRefresh: () => void;
	disableUpload?: boolean;
	hidePath?: boolean;
}

export default function FilesExplorerHeader({
	folderPath,
	currentPath,
	setCurrentPath,
	currentSearch,
	setCurrentSearch,
	setSelectedFile,
	updateRefresh,
	disableUpload,
	hidePath
}: FilesExplorerHeaderProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	function goDirectoryUp() {
		const folders = currentPath.split('\\');
		folders.pop();
		const path = folders.join('\\');
		setCurrentPath(path);
		setSelectedFile(undefined);
	}

	function handleUploadFiles(event) {
		uploadFiles({
			path: encodeURIComponent(currentPath),
			files: event.target.files,
			dispatch,
			successHandler: () => {
				dispatch(alertLocalizedMsg(t, 'ALERT_ADD_SUCCESS', 'success'));
				updateRefresh();
			}
		});

		event.persist();
		event.target.value = null;
	}

	return (
		<Box className="p-6" borderBottom={theme => `1px solid ${theme.palette.divider}`}>
			<Grid container justifyContent="space-between" alignItems="center" spacing={1}>
				<Grid>
					<IconButton className="h-36 w-36 p-0" onClick={goDirectoryUp} disabled={currentPath === folderPath}>
						<Icon icon="arrow-up" />
					</IconButton>
					<IconButton className="h-36 w-36 p-0" onClick={updateRefresh}>
						<Icon icon="refresh" />
					</IconButton>
					{!disableUpload && (
						<>
							<input
								id="upload-button-file"
								className="hidden"
								name="files[]"
								type="file"
								multiple
								onChange={handleUploadFiles}
							/>
							<label htmlFor="upload-button-file">
								<Button
									className="ml-8"
									variant="text"
									color="inherit"
									startIcon={<Icon icon="upload" />}
									component="span"
								>
									{t('FILE_EXPLORER_UPLOAD_FILE')}
								</Button>
							</label>
						</>
					)}
				</Grid>
				<Grid>
					<BaseSearch value={currentSearch} handleSubmit={setCurrentSearch} />
				</Grid>
				<Grid size={12}>
					{!hidePath && (
						<Typography className="break-words px-8" variant="subtitle2">{`${currentPath}\\`}</Typography>
					)}
				</Grid>
			</Grid>
		</Box>
	);
}
